import React, { useEffect, useRef, useState } from "react";
import ProductImage from "../common/ProductImage";
import "./PreviewOrder.css";

const STORE_NAME_KEYS = [
  "storeName",
  "store_name",
  "business_segment_name",
  "businessSegmentName",
  "merchant_name",
  "merchantName",
  "branch_name",
  "branchName",
  "name",
];

const ORDER_ID_KEYS = [
  "order_id",
  "orderId",
  "id",
  "order_no",
  "orderNo",
  "merchant_order_id",
  "merchantOrderId",
  "order_number",
  "orderNumber",
];

const ORDER_ACTION_ID_KEYS = [
  "id",
  "booking_order_id",
  "bookingOrderId",
  "merchant_order_id",
  "merchantOrderId",
  "order_id",
  "orderId",
  "order_no",
  "orderNo",
  "order_number",
  "orderNumber",
];

const AMOUNT_KEYS = [
  "final_total",
  "finalTotal",
  "finalAmount",
  "final_amount",
  "total_amount",
  "totalAmount",
  "grand_total",
  "grandTotal",
  "payable_amount",
  "payableAmount",
  "cart_amount",
  "cartAmount",
  "order_amount",
  "orderAmount",
];

const DELIVERED_DATE_KEYS = [
  "deliveredOn",
  "delivered_on",
  "delivered_date",
  "deliveredDate",
  "delivery_date",
  "deliveryDate",
  "order_date",
  "orderDate",
  "created_at",
  "createdAt",
];

const PRODUCT_LIST_KEYS = [
  "products",
  "product",
  "items",
  "order_items",
  "orderItems",
  "order_products",
  "orderProducts",
  "product_details",
  "productDetails",
  "cart_items",
  "cartItems",
  "cell_contents",
];

const PRODUCT_SECTION_KEYS = [
  "confirmed_items",
  "confirmedItems",
  "pending_items",
  "pendingItems",
  "substitute_items",
  "substituteItems",
];

const PRODUCT_NAME_KEYS = [
  "name",
  "title",
  "product_name",
  "productName",
  "product_title",
  "productTitle",
];

const PRODUCT_IMAGE_KEYS = [
  "image",
  "images",
  "product_image",
  "productImage",
  "product_cover_image",
  "productCoverImage",
  "cart_product_image",
  "cartProductImage",
  "image_url",
  "imageUrl",
  "thumbnail",
  "thumb",
];

const INTERACTIVE_TARGET_SELECTOR = "button, a, input, select, textarea, [role='button']";

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const parseJsonValue = (value) => {
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

const isImageSource = (value) =>
  typeof value === "string" &&
  (/^(https?:|data:image|blob:|\/|assets\/)/i.test(value.trim()) ||
    /\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i.test(value.trim()));

const getFirstValue = (sources, keys, fallback = "") => {
  for (const source of sources) {
    const parsedSource = parseJsonValue(source);

    if (!isPlainObject(parsedSource)) continue;

    const matchedKey = keys.find((key) => hasValue(parsedSource?.[key]));
    if (matchedKey) return parsedSource[matchedKey];
  }

  return fallback;
};

const getListFromValue = (value) => {
  const parsedValue = parseJsonValue(value);

  if (Array.isArray(parsedValue)) return parsedValue;

  if (!isPlainObject(parsedValue)) return [];

  const sectionItems = PRODUCT_SECTION_KEYS.flatMap((key) => getListFromValue(parsedValue?.[key]));
  if (sectionItems.length) return sectionItems;

  for (const key of PRODUCT_LIST_KEYS) {
    const list = getListFromValue(parsedValue?.[key]);
    if (list.length) return list;
  }

  return [];
};

const getImageProductsFromValue = (value) => {
  const parsedValue = parseJsonValue(value);

  if (Array.isArray(parsedValue)) {
    return parsedValue.flatMap((item) => {
      const parsedItem = parseJsonValue(item);

      if (typeof parsedItem === "string") {
        return isImageSource(parsedItem) ? [parsedItem] : [];
      }

      if (isPlainObject(parsedItem)) {
        const image = normalizeImage(getFirstValue([parsedItem], PRODUCT_IMAGE_KEYS, ""));
        return image ? [parsedItem] : [];
      }

      return [];
    });
  }

  return normalizeImage(parsedValue) ? [parsedValue] : [];
};

const getOrderProducts = (order) => {
  const sources = [
    order?.product_data,
    order?.productData,
    order?.receipt,
    order?.order_details,
    order?.orderDetails,
    order,
  ];

  const orderImages = getImageProductsFromValue(order?.images);
  if (orderImages.length) return orderImages;

  for (const source of sources) {
    const list = getListFromValue(source);
    if (list.length) return list;
  }

  return getFirstValue([order], PRODUCT_IMAGE_KEYS, "") ? [order] : [];
};

const normalizeImage = (value) => {
  const parsedValue = parseJsonValue(value);

  if (typeof parsedValue === "string") return isImageSource(parsedValue) ? parsedValue : "";
  if (Array.isArray(parsedValue)) {
    const image = parsedValue.map(normalizeImage).find(Boolean);
    return image || "";
  }

  return normalizeImage(getFirstValue([parsedValue], PRODUCT_IMAGE_KEYS, ""));
};

const normalizeProduct = (product) => {
  const productSource = parseJsonValue(product);
  const nestedProduct = parseJsonValue(productSource?.product);
  const sources = [productSource, nestedProduct];
  const directImage = typeof productSource === "string" ? productSource : "";

  return {
    name: getFirstValue(sources, PRODUCT_NAME_KEYS, "Product"),
    image: normalizeImage(getFirstValue(sources, PRODUCT_IMAGE_KEYS, directImage)),
  };
};

const formatAmount = (value, order) => {
  if (!hasValue(value)) return "";

  const rawValue = String(value).trim();
  if (/[a-zA-Z₹$€£]/.test(rawValue)) return rawValue;

  const numericValue = Number(value);
  const currency = getFirstValue(
    [order],
    ["currency", "currency_code", "currencyCode"],
    "AED"
  );

  return Number.isFinite(numericValue)
    ? `${currency} ${numericValue.toFixed(2)}`
    : `${currency} ${rawValue}`;
};

const normalizeOrder = (order, index) => {
  const orderSource = parseJsonValue(order);
  const receipt = parseJsonValue(orderSource?.receipt);
  const details = parseJsonValue(orderSource?.order_details || orderSource?.orderDetails);
  const sources = [orderSource, receipt, details];
  const products = getOrderProducts(orderSource).map(normalizeProduct);

  return {
    actionId: getFirstValue(sources, ORDER_ACTION_ID_KEYS, `order-${index + 1}`),
    order_id: getFirstValue(sources, ORDER_ID_KEYS, `order-${index + 1}`),
    storeName: getFirstValue(sources, STORE_NAME_KEYS, "Store"),
    deliveredOn: getFirstValue(sources, DELIVERED_DATE_KEYS, "-"),
    final_total: formatAmount(getFirstValue(sources, AMOUNT_KEYS, ""), orderSource),
    products,
    rawOrder: orderSource,
  };
};

const PreviewOrder = ({ orders = [], onOrderAgain, orderAgainLoadingId = "" }) => {
  const scrollRef = useRef(null);
  const dragState = useRef({
    active: false,
    dragged: false,
    pointerId: null,
    scrollLeft: 0,
    startX: 0,
  });
  const [isDragging, setIsDragging] = useState(false);
  const normalizedOrders = Array.isArray(orders) ? orders.map(normalizeOrder) : [];
  const hasOrders = normalizedOrders.length > 0;

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return undefined;

    const handleWheel = (event) => {
      if (event.ctrlKey || !event.cancelable || scrollElement.scrollWidth <= scrollElement.clientWidth) return;

      const maxScrollLeft = scrollElement.scrollWidth - scrollElement.clientWidth;
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      const nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, scrollElement.scrollLeft + delta));

      if (nextScrollLeft === scrollElement.scrollLeft) return;

      event.preventDefault();
      scrollElement.scrollLeft = nextScrollLeft;
    };

    // React's delegated wheel listener is passive, so cancellation needs a native listener.
    scrollElement.addEventListener("wheel", handleWheel, { passive: false });
    return () => scrollElement.removeEventListener("wheel", handleWheel);
  }, [hasOrders]);

  if (!hasOrders) {
    return null;
  }

  const stopDrag = () => {
    const scrollElement = scrollRef.current;

    if (!dragState.current.active) return;

    if (scrollElement && dragState.current.pointerId !== null) {
      scrollElement.releasePointerCapture?.(dragState.current.pointerId);
    }

    dragState.current.active = false;
    dragState.current.pointerId = null;
    setIsDragging(false);
  };

  const handlePointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (event.target.closest?.(INTERACTIVE_TARGET_SELECTOR)) {
      dragState.current.dragged = false;
      return;
    }

    const scrollElement = scrollRef.current;

    if (!scrollElement || scrollElement.scrollWidth <= scrollElement.clientWidth) return;

    dragState.current = {
      active: true,
      dragged: false,
      pointerId: event.pointerId,
      scrollLeft: scrollElement.scrollLeft,
      startX: event.clientX,
    };
    scrollElement.setPointerCapture?.(event.pointerId);
    setIsDragging(true);
  };

  const handlePointerMove = (event) => {
    const scrollElement = scrollRef.current;

    if (!scrollElement || !dragState.current.active) return;

    const walk = event.clientX - dragState.current.startX;

    if (Math.abs(walk) > 3) {
      dragState.current.dragged = true;
    }

    scrollElement.scrollLeft = dragState.current.scrollLeft - walk;
  };

  const handleClickCapture = (event) => {
    if (!dragState.current.dragged) return;
    if (event.target.closest?.(INTERACTIVE_TARGET_SELECTOR)) {
      dragState.current.dragged = false;
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    dragState.current.dragged = false;
  };

  return (
    <section className="preview-orders-section">
      <div className="container container-lg">
        <h5 className="preview-orders-title">Previous Orders</h5>
        <div
          ref={scrollRef}
          className={`preview-orders-scroll${isDragging ? " is-dragging" : ""}`}
          aria-label="Previous orders"
          onClickCapture={handleClickCapture}
          onPointerCancel={stopDrag}
          onPointerDown={handlePointerDown}
          onPointerLeave={stopDrag}
          onPointerMove={handlePointerMove}
          onPointerUp={stopDrag}
        >
          {normalizedOrders.map((order, index) => {
            const isOrderAgainLoading = Boolean(
              orderAgainLoadingId && String(orderAgainLoadingId) === String(order.actionId)
            );

            return (
              <article className="preview-order-card" key={`${order.order_id || "order"}-${index}`}>
                <div className="preview-order-main">
                  <h6>{order.storeName}</h6>
                  <div className="preview-order-products" aria-label="Order products">
                    {order.products.map((product, productIndex) => (
                      <span className="preview-order-product" key={`${product.name || "item"}-${productIndex}`}>
                        <ProductImage src={product.image} alt={product.name || "Product"} />
                      </span>
                    ))}
                  </div>
                  <div className="preview-order-meta">
                    <span>Order Id: {order.order_id}</span>
                    <span>Final Amount: {order.final_total || "-"}</span>
                  </div>
                </div>
                <div className="preview-order-side">
                  <div className="preview-order-date">
                    <span>Delivered On</span>
                    <strong>{order.deliveredOn}</strong>
                  </div>
                  <button
                    type="button"
                    disabled={isOrderAgainLoading}
                    onClick={() => onOrderAgain?.(order.rawOrder, order.actionId)}
                  >
                    {isOrderAgainLoading ? "Adding..." : "Order Again"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PreviewOrder;
